/*
 * Candles by Midnight — Build Your Own Midnight Box.
 *
 * Adds each chosen candle to the cart as its own line item in the chosen
 * fragrance, sharing a `_box` id so the group can be identified in the order.
 * Underscore-prefixed properties are hidden from the customer in cart and
 * checkout, which is what we want for a grouping key.
 *
 * A single /cart/add.js call carries all items, so the whole box either lands
 * or it does not — no half-added box for the customer to untangle.
 */
if (!customElements.get('midnight-box')) {
  customElements.define(
    'midnight-box',
    class MidnightBox extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '₹{{amount}}';
        this.boxVariant = this.dataset.boxVariant || null;
        this.boxPrice = parseInt(this.dataset.boxPrice, 10) || 0;

        this.cards = Array.from(this.querySelectorAll('[data-product]'));
        this.countEl = this.querySelector('[data-count]');
        this.totalEl = this.querySelector('[data-total]');
        this.addBtn = this.querySelector('[data-add]');
        this.statusEl = this.querySelector('[data-status]');
        this.noteEl = this.querySelector('[data-note]');

        this.size = parseInt(this.querySelector('[data-size]:checked').value, 10);
        this.selected = [];

        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.addBtn.addEventListener('click', this.submit.bind(this));

        this.render();
      }

      onChange(event) {
        if (event.target.matches('[data-size]')) {
          this.size = parseInt(event.target.value, 10);
          // Shrinking the box must not silently keep extra candles.
          if (this.selected.length > this.size) {
            this.selected.slice(this.size).forEach((card) => this.deselect(card));
            this.selected = this.selected.slice(0, this.size);
          }
          this.render();
        }
      }

      onClick(event) {
        var chip = event.target.closest('[data-filter]');
        if (chip) return this.filter(chip);

        var pick = event.target.closest('[data-pick]');
        if (pick) return this.toggle(pick.closest('[data-product]'));
      }

      filter(chip) {
        this.querySelectorAll('[data-filter]').forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
        });
        var range = chip.dataset.filter;
        this.cards.forEach(function (card) {
          if (range === 'all') {
            card.hidden = false;
            return;
          }
          var bounds = range.split('-').map(Number);
          var price = parseFloat(card.dataset.price);
          card.hidden = !(price >= bounds[0] && price <= bounds[1]);
        });
      }

      toggle(card) {
        var index = this.selected.indexOf(card);
        if (index > -1) {
          this.selected.splice(index, 1);
          this.deselect(card);
        } else {
          if (this.selected.length >= this.size) {
            this.say(
              'Your box holds ' + this.size + '. Remove one, or choose a larger box.'
            );
            return;
          }
          this.selected.push(card);
          card.querySelector('[data-pick]').setAttribute('aria-pressed', 'true');
          card.querySelector('[data-fragrance]').hidden = false;
        }
        this.render();
      }

      deselect(card) {
        card.querySelector('[data-pick]').setAttribute('aria-pressed', 'false');
        card.querySelector('[data-fragrance]').hidden = true;
      }

      money(paise) {
        return this.moneyFormat.replace(
          /\{\{\s*amount[^}]*\}\}/,
          (paise / 100).toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        );
      }

      render() {
        var total = this.selected.reduce(function (sum, card) {
          return sum + parseFloat(card.dataset.price) * 100;
        }, 0);
        if (this.selected.length && this.boxVariant) total += this.boxPrice;

        this.countEl.textContent =
          this.selected.length + ' of ' + this.size + ' chosen';
        this.totalEl.textContent = this.money(total);

        var complete = this.selected.length === this.size;
        this.addBtn.disabled = !complete;
        this.addBtn.textContent = complete
          ? 'Add box to cart'
          : 'Choose ' + (this.size - this.selected.length) + ' more';
      }

      say(message, isError) {
        this.statusEl.textContent = message;
        this.statusEl.dataset.state = isError ? 'error' : '';
      }

      submit() {
        var self = this;
        // A shared id lets the merchant see which lines belong to one box.
        var boxId = 'MB-' + Date.now().toString(36).toUpperCase();
        var note = this.noteEl ? this.noteEl.value.trim() : '';

        var items = this.selected.map(function (card) {
          var select = card.querySelector('[data-fragrance]');
          var props = { _box: boxId, 'Part of': 'Midnight Box' };
          if (note) props['Gift note'] = note.slice(0, 250);
          return { id: Number(select.value), quantity: 1, properties: props };
        });

        if (this.boxVariant) {
          items.push({
            id: Number(this.boxVariant),
            quantity: 1,
            properties: { _box: boxId, 'Part of': 'Midnight Box' },
          });
        }

        this.addBtn.disabled = true;
        this.say('Packing your box…');

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .then(function (response) {
            if (!response.ok) throw new Error('cart');
            return response.json();
          })
          .then(function () {
            self.say('Added. Your Midnight Box is in the cart.');
            // Let the drawer refresh itself rather than hard-navigating.
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              document.dispatchEvent(new CustomEvent('cbm:cart-updated'));
              window.location.href = window.Shopify.routes.root + 'cart';
            } else {
              window.location.href = window.Shopify.routes.root + 'cart';
            }
          })
          .catch(function () {
            self.addBtn.disabled = false;
            self.say(
              'We could not add that box. Please try again, or message us on WhatsApp.',
              true
            );
          });
      }
    }
  );
}
